/*
 * Vd'O search autocomplete — added 2026-08-16
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 * Shopify's Predictive Search API does not support Japanese: /search/suggest
 * returns HTTP 417 on the JA storefront, and matches nothing on /en because the
 * shop's primary locale is Japanese. Turbo's stock handler had no .catch(), so
 * the failure left the loading spinner running forever.
 *
 * This re-points the same dropdown at Turbo's own JSON search view
 * (/search?q=...&view=json), which uses the ordinary search endpoint and works
 * in Japanese. Input, container and CSS are untouched.
 *
 * WHY THE TOLERANT PARSER
 * Storefront apps (the Shopify Inbox chat widget among them) inject their own
 * markup into every storefront response — including this one, even though the
 * JSON template sets layout: false. The body therefore arrives as valid JSON
 * with HTML appended, and JSON.parse() chokes on the trailing '<div id="s...'.
 * extractJson() walks the leading JSON object with a brace counter (string- and
 * escape-aware) and discards anything after it.
 *
 * LAYOUT AND ORDER
 * Results are split into two headed groups, mirroring the markup Turbo's own
 * sections/predictive-search.liquid emits, so no new CSS is needed for them:
 *   .predictive-search__product-suggestions      — in-stock, then sold-out
 *   .predictive-search__article-page-suggestions — articles, then pages
 * Heading text comes from window.vdoSearchHeadings, resolved from the theme's
 * locale files in snippets/search-form.liquid (this file cannot call `| t`).
 * Neither group is capped: every result the search returns is listed, and the
 * dropdown scrolls — Turbo sets --search-results-max-height on the container.
 *
 * THUMBNAIL SIZING
 * injectStyles() adds one small stylesheet, once per page. The catalogue's
 * images are not a consistent aspect ratio (mostly ~410x800 portrait bottles,
 * but books, oil tins and beer differ), and the wrap div is sized by WIDTH, so
 * a squarer image rendered taller and the rows came out ragged. Fixing the box
 * height and letting the image letterbox inside it with object-fit: contain
 * lines every row up without cropping any label. THUMB_HEIGHT below is the one
 * number to change. Styles live here rather than in assets/styles.css, which is
 * 360KB and shared with everything else.
 */
(function () {
  'use strict';

  var THUMB_HEIGHT = '72px';
  var STYLE_ID = 'vdo-search-autocomplete-css';

  function translations() {
    return (window.PXUTheme && window.PXUTheme.translation) || {};
  }

  function headings() {
    return window.vdoSearchHeadings || {};
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * One stylesheet for the whole page, however many search forms it renders
   * (header, mobile, blog). Scoped to this dropdown's own thumbnails so nothing
   * else on the storefront is touched.
   */
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var css =
      '.predictive-search__list-item-thumbnail .image-element__wrap{' +
        'height:' + THUMB_HEIGHT + ';' +
        'padding:0 !important;' +   // Turbo reserves space with a padding ratio
        'display:flex;' +
        'align-items:center;' +
        'justify-content:center;' +
        'overflow:hidden;' +
      '}' +
      '.predictive-search__list-item-thumbnail .image-element__wrap img{' +
        'width:100%;' +
        'height:100%;' +
        'object-fit:contain;' +     // letterbox, never crop the label
        'object-position:center;' +
        'display:block;' +
      '}';

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
  }

  /**
   * Returns the substring spanning the first complete top-level {...} object,
   * ignoring braces that appear inside JSON strings. Null if none is found.
   */
  function extractJson(text) {
    var start = text.indexOf('{');
    if (start === -1) return null;

    var depth = 0;
    var inString = false;
    var escaped = false;

    for (var i = start; i < text.length; i++) {
      var char = text.charAt(i);

      if (escaped) {
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        inString = !inString;
      } else if (!inString) {
        if (char === '{') {
          depth++;
        } else if (char === '}') {
          depth--;
          if (depth === 0) return text.slice(start, i + 1);
        }
      }
    }

    return null;
  }

  function priceMarkup(item) {
    var t = translations();

    if (item.available === false) {
      return '<span class="sold_out_price">' + esc(t.sold_out_text || '') + '</span>';
    }

    var html = '<span class="current_price">';
    if (item.price_varies && t.from_text) {
      html += '<small class="from"><em>' + esc(t.from_text) + '</em></small> ';
    }
    html += '<span class="money">' + esc(item.price) + '</span></span>';

    // raw_compare is compare_at_price_min (see the JSON template's comment) so
    // this only fires on a genuine single-bottle markdown. A null compare-at
    // arrives as 0 and fails the test, as does an equal one.
    var compare = parseFloat(item.raw_compare);
    var current = parseFloat(item.raw_price);
    if (!isNaN(compare) && !isNaN(current) && compare > current) {
      html += ' <span class="was_price"><span class="money">' + esc(item.compare) + '</span></span>';
    }

    return html;
  }

  function itemMarkup(item) {
    var thumbnail = '';
    if (item.thumbnail) {
      // .image-element__wrap is what Turbo's CSS sizes against; a bare <img>
      // here renders at full width and swamps the dropdown. Dimensions come
      // from injectStyles(), not from inline styles, so they stay overridable.
      thumbnail =
        '<figure class="predictive-search__list-item-thumbnail">' +
        '<div class="image-element__wrap">' +
        '<img src="' + esc(item.thumbnail) + '" alt="' + esc(item.title) + '" loading="lazy" />' +
        '</div>' +
        '</figure>';
    }

    var detail = '';
    if (item.object_type === 'product') {
      detail = '<p class="predictive-search__list-item-price">' + priceMarkup(item) + '</p>';
    } else if (item.text_content) {
      detail = '<p class="predictive-search__list-item-excerpt">' + esc(item.text_content) + '</p>';
    }

    return (
      '<li class="predictive-search__list-item" role="listitem">' +
      '<a class="predictive-search__list-item-anchor" href="' + esc(item.url) + '">' +
      thumbnail +
      '<div class="predictive-search__list-item-content">' +
      '<p class="predictive-search__list-item-title">' + esc(item.title) + '</p>' +
      detail +
      '</div></a></li>'
    );
  }

  /** One headed block. Returns '' for an empty group so no stray heading shows. */
  function groupMarkup(className, heading, items) {
    if (!items.length) return '';

    var head = heading
      ? '<h2 class="predictive-search__heading">' + esc(heading) + '</h2>'
      : '';

    return (
      '<div class="' + className + '">' +
      head +
      '<ul class="predictive-search__results-list" role="list">' +
      items.map(itemMarkup).join('') +
      '</ul></div>'
    );
  }

  function searchButton() {
    var t = translations();
    return (
      '<button class="predictive-search__search-button global-button" type="submit" data-search-button>' +
      esc(t.all_results || 'Search') +
      '</button>'
    );
  }

  function noResults() {
    var t = translations();
    return (
      '<div class="predictive-search__no-results">' +
      '<p class="predictive-search__no-results-text">' + esc(t.no_results || '') + '</p>' +
      '</div>' +
      searchButton()
    );
  }

  function buildMarkup(data) {
    var items = (data && data.results) || [];
    if (!items.length) return noResults();

    var products = [];
    var posts = [];
    var pages = [];

    items.forEach(function (item) {
      if (item.object_type === 'product') {
        products.push(item);
      } else if (item.object_type === 'article') {
        posts.push(item);
      } else {
        pages.push(item);
      }
    });

    // Within the products, buyable bottles ahead of sold-out ones. Two passes
    // rather than .sort() so the search engine's own relevance order is
    // preserved inside each group.
    var inStock = products.filter(function (item) { return item.available !== false; });
    var soldOut = products.filter(function (item) { return item.available === false; });

    var h = headings();
    var otherHeading;
    if (posts.length && pages.length) {
      otherHeading = h.pagesAndPosts;
    } else if (posts.length) {
      otherHeading = h.posts;
    } else {
      otherHeading = h.pages;
    }

    return (
      groupMarkup(
        'predictive-search__product-suggestions',
        h.products,
        inStock.concat(soldOut)
      ) +
      groupMarkup(
        'predictive-search__article-page-suggestions',
        otherHeading,
        posts.concat(pages)
      ) +
      searchButton()
    );
  }

  function patch() {
    var ps = window.predictiveSearch;
    if (!ps || ps.__vdoJsonPatched) return !!ps;

    ps.__vdoJsonPatched = true;
    injectStyles();

    ps._fetchResults = function (term, form, results, resultsContainer) {
      var self = this;
      var routes = (window.PXUTheme && window.PXUTheme.routes) || {};
      var base = routes.search_url || '/search';
      var typeParam = form.classList.contains('search-form--blog') ? '&type=article' : '';
      var url = base + '?q=' + encodeURIComponent(term) + '&view=json' + typeParam;

      resultsContainer.setAttribute('data-loading', 'true');

      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (text) {
          var json = extractJson(text);
          if (!json) throw new Error('No JSON object found in search response');
          self._renderResults(buildMarkup(JSON.parse(json)), results, resultsContainer);
        })
        .catch(function (error) {
          // Never leave the spinner running — that was the original bug.
          self._renderResults(noResults(), results, resultsContainer);
          if (window.console && window.console.error) {
            window.console.error('[search-autocomplete]', error);
          }
        });
    };

    return true;
  }

  if (!patch()) {
    document.addEventListener('DOMContentLoaded', patch);
  }
})();
